import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Severity,
  type SeverityType,
} from '@kirin/api-client/contracts/shared/severity';
import { type JSX, useMemo, useState } from 'react';
import { SEVERITY_OPTIONS } from '../policylist/helpers';

export interface RuleFormValues {
  pattern: string;
  description: string;
  severity: SeverityType;
}

interface RegexEditComponentProps {
  inputLabel?: string;
  descriptionLabel?: string;
  severityLabel?: string;
  /** Pre-populate the form (edit mode). Omit for an empty add form. */
  initialValues?: RuleFormValues;
  /** Submit button text. Defaults to "Add Rule"; pass "Edit Rule" when editing. */
  submitLabel?: string;
  /** Focus the Rule field on mount (e.g. when an Edit drawer opens). */
  autoFocus?: boolean;
  onSubmit: (rule: RuleFormValues) => void;
  onCancel: () => void;
  disabled?: boolean;
  /** Support role: keep the form and Cancel interactive, only disable Submit. */
  submitDisabled?: boolean;
  /**
   * Extra check on the rule. Returns why the rule is unusable, or `undefined`
   * if it is fine. It runs as the rule is typed: while it returns a reason, the
   * reason is shown and the submit button is disabled. Chat Security uses it to
   * block a rule its scanner could never run (KIR-4928).
   */
  validatePattern?: (pattern: string) => string | undefined;
  /** Longest rule accepted. Defaults to 500. */
  maxPatternLength?: number;
}

const DEFAULT_MAX_PATTERN_LENGTH = 500;

export function RegexEditComponent({
  inputLabel = 'Rule',
  descriptionLabel = 'Description',
  severityLabel = 'Severity',
  initialValues,
  submitLabel = 'Add Rule',
  autoFocus = false,
  onSubmit,
  onCancel,
  disabled = false,
  submitDisabled = false,
  validatePattern,
  maxPatternLength = DEFAULT_MAX_PATTERN_LENGTH,
}: RegexEditComponentProps): JSX.Element {
  const [pattern, setPattern] = useState(initialValues?.pattern ?? '');
  const [description, setDescription] = useState(
    initialValues?.description ?? ''
  );
  const [severity, setSeverity] = useState<SeverityType>(
    initialValues?.severity ?? Severity.Medium
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Why the rule as typed can never be saved. Checked live so the submit
  // button stays disabled until it is fixed. An empty rule is not a rejection:
  // "Rule is required" is still reported on submit, like the other fields.
  const patternRejection = useMemo((): string | undefined => {
    const trimmed = pattern.trim();
    if (!trimmed) {
      return undefined;
    }
    if (pattern.length > maxPatternLength) {
      return `Rule must be ${maxPatternLength} characters or less`;
    }
    try {
      new RegExp(trimmed);
    } catch {
      return 'Invalid regular expression';
    }
    return validatePattern?.(trimmed);
  }, [pattern, maxPatternLength, validatePattern]);

  const patternError = patternRejection ?? errors.pattern;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate rule (regex)
    if (!pattern.trim()) {
      newErrors.pattern = 'Rule is required';
    } else if (patternRejection) {
      newErrors.pattern = patternRejection;
    }

    // Validate description
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length > 200) {
      newErrors.description = 'Description must be 200 characters or less';
    }

    // Validate severity
    if (!severity) {
      newErrors.severity = 'Severity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (): void => {
    if (validateForm()) {
      onSubmit({
        pattern: pattern.trim(),
        description: description.trim(),
        severity,
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Rule Input */}
      <div className="space-y-2">
        <Label htmlFor="pattern">{inputLabel}</Label>
        <Input
          id="pattern"
          value={pattern}
          onChange={e => {
            setPattern(e.target.value);
            // Drop a stale "Rule is required" once the rule has content;
            // whitespace alone is still empty.
            if (e.target.value.trim()) {
              setErrors(({ pattern: _stale, ...rest }) => rest);
            }
          }}
          placeholder="e.g., eval\\s*\\("
          className={patternError ? 'border-red-500' : ''}
          disabled={disabled}
          maxLength={maxPatternLength}
          autoFocus={autoFocus}
          aria-invalid={patternError ? true : undefined}
          aria-describedby={patternError ? 'pattern-error' : undefined}
        />
        {patternError && (
          // role="alert": the reason now appears while typing and disables
          // the submit button, so announce it rather than leave it silent.
          <div id="pattern-error" className="text-xs text-red-500" role="alert">
            {patternError}
          </div>
        )}
      </div>

      {/* Description Input */}
      <div className="space-y-2">
        <Label htmlFor="description">{descriptionLabel}</Label>
        <Textarea
          id="description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Brief description..."
          className={cn(
            'min-h-9 resize-none',
            errors.description && 'border-red-500'
          )}
          disabled={disabled}
          maxLength={200}
          rows={1}
        />
        {errors.description && (
          <div className="text-xs text-red-500">{errors.description}</div>
        )}
      </div>

      {/* Severity Select */}
      <div className="space-y-2">
        <Label htmlFor="severity">{severityLabel}</Label>
        <Select
          value={severity}
          onValueChange={value => setSeverity(value as SeverityType)}
          disabled={disabled}
        >
          <SelectTrigger id="severity" className="w-full">
            <SelectValue placeholder="Select severity" />
          </SelectTrigger>
          <SelectContent>
            {SEVERITY_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.severity && (
          <div className="text-xs text-red-500">{errors.severity}</div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={
            disabled || submitDisabled || patternRejection !== undefined
          }
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
